import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";
import { LoadingStage } from "src/Interfaces/shared";
import {
    ITrip,
    ITripItem,
    ITripTraveller,
    TripOptionalData,
    ITripPlannerState,
    ITripSummary,
    IQuote,
    IUpdateCartItemData,
    TripItemAvailability,
    BookingData,
    ICartItemData,
    ICustomerDetail,
} from "@interfaces";

/**
 * Pull a user-facing message off an unknown error from one of our axios
 * clients. Backend errors carry `response.data.title` (RFC7807-ish), and
 * generic JS errors carry `.message`. Falls back to the supplied default.
 */
const extractApiMessage = (e: unknown, fallback: string): string => {
    if (e && typeof e === "object") {
        const err = e as {
            response?: { data?: { title?: unknown; detail?: unknown; errors?: unknown }; status?: number };
            message?: unknown;
        };
        // Surface as much detail as possible into the console so a generic
        // "Validation Error." toast can be diagnosed from devtools without
        // another round-trip with the user.
        try {
            // eslint-disable-next-line no-console
            console.error(
                "[tripPlanner] API error",
                "status=", err.response?.status,
                "body=", JSON.stringify(err.response?.data),
                "message=", err.message,
            );
        } catch {
            /* logging is best-effort */
        }
        const data = err.response?.data;
        if (data && typeof data === "object") {
            const errors = (data as { errors?: unknown }).errors;
            // ASP.NET-style { errors: { Field: ["msg"] } } → flatten to first message.
            if (errors && typeof errors === "object") {
                const fieldMsgs: string[] = [];
                for (const [field, msgs] of Object.entries(errors as Record<string, unknown>)) {
                    if (Array.isArray(msgs) && msgs.length && typeof msgs[0] === "string") {
                        fieldMsgs.push(`${field}: ${msgs[0]}`);
                    }
                }
                if (fieldMsgs.length) return fieldMsgs.join("; ");
            }
            const detail = (data as { detail?: unknown }).detail;
            if (typeof detail === "string" && detail) return detail;
            const title = (data as { title?: unknown }).title;
            if (typeof title === "string" && title) return title;
        }
        if (typeof err.message === "string" && err.message) return err.message;
    }
    return fallback;
};
import { quoteApi } from "src/Api/quoteApi";
import { cartApi } from "src/Api/cartApi";
import { toursApi } from "src/Api/toursApi";
import { redeemerApi } from "src/Api/redeemers";
import {
    newTripItemId,
    quotesToTripSummaries,
    quoteToTrip,
    tripItemToAddPayload,
    tripToNewQuotePayload,
    extractNewQuoteId,
    tripToAddItemPayloads,
    tripToCartPayloads,
    ensureRosterCount,
    dateForDayIndex,
    isTravellerComplete,
    travellerToCustomerData,
    selectedTravellersForItem,
    cleanOptionalData,
    migrateTripTravellers,
    buildTripShapeFields,
    rosterToCustomerInfos,
    customerStableId,
    pairTripItemsToQuoteItems,
    buildItemBookingData,
} from "src/Utilities/tripPlannerHelpers";
import { shouldCheckAvailabilityByGroup } from "src/Utilities/availabilityEndpoint";
import { RootState } from ".";

const todayIso = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const emptyTrip = (): ITrip => ({
    id: undefined,
    name: "My Trip",
    startDate: todayIso(),
    durationDays: 7,
    numTravellers: 1,
    travellers: [],
    items: [],
});

const initialState: ITripPlannerState = {
    current: emptyTrip(),
    savedTrips: [],
    loadStage: LoadingStage.NOT_INITIALIZED,
    saveStage: LoadingStage.NOT_INITIALIZED,
    convertStage: LoadingStage.NOT_INITIALIZED,
};

// ---- Thunks ----------------------------------------------------------------

export const checkTripItemAvailability = createAsyncThunk<
    {
        itemId: string;
        version: number;
        status: TripItemAvailability;
        message?: string;
        /** Index of the row matching the item's start date in the availability response. */
        availIndex?: number;
        /**
         * BookingTimeID from the matched availability row. The FT cart/quote
         * API rejects a payload whose `timeId` doesn't correspond to a real
         * slot; the drag-drop quick-add doesn't ask the user to pick a time,
         * so we auto-adopt the first available slot for that date. Items
         * with no time component (sentinel "0") keep the sentinel.
         */
        bookingTimeId?: string;
    },
    { itemId: string },
    { state: { tripPlanner: ITripPlannerState } }
>("tripPlanner/checkAvailability", async ({ itemId }, { getState }) => {
    const trip = getState().tripPlanner.current;
    const item = trip.items.find((i) => i.id === itemId);
    if (!item) {
        return { itemId, version: -1, status: "unknown" as TripItemAvailability };
    }
    const version = item.availabilityVersion;

    // Build the Date via moment so it represents local midnight; `new Date("YYYY-MM-DD")`
    // parses as UTC and shifts the booking date in TZs west of UTC.
    const startDate = moment(trip.startDate, "YYYY-MM-DD")
        .add(item.dayIndex, "days")
        .toDate();
    const ppdid = String(item.productPricesDetailsId);
    const timeId = item.bookingTimeId ? String(item.bookingTimeId) : "0";
    // Accommodation needs every night to be bookable. A multi-day tour
    // (e.g. "2 Day K'gari") is a single booking on its start date — the
    // following days are part of the same booking, not independent
    // availability slots — so only the first day needs to be checked.
    const span = Math.max(1, item.durationDays);
    const isAccommodation = item.productClass === "accommodation";
    const days = isAccommodation ? span : 1;
    try {
        // Accommodation must pick the SAME availability endpoint the checkout
        // calendar uses, branching on `shouldCheckAvailabilityByGroup`
        // (apiProviderId > 0 && groupFaresForAvailabilityCheck):
        //   - SiteMinder accommodation → checkAvailabilityByProductAndRange
        //     keyed on (productId, fareTypeId) over the night range. The trip
        //     tile already carries both keys, so we can re-check the moved date
        //     explicitly without re-opening the calendar.
        //   - SOA / Generic accommodation (and all non-accommodation items) →
        //     checkavailabilityrange keyed on productPricesDetailsId.
        // Without this branch, SiteMinder accommodation queried the SOA-only
        // range endpoint and always came back empty, so every drag-drop move
        // reported "unavailable".
        // Guard: the by-product-and-range URL embeds the fareTypeId as a path
        // segment, so an empty/missing fareTypeId would build a malformed
        // ".../{productId}//{start}/{end}" call. Fall back to the range
        // endpoint in that case rather than firing a bad request.
        const hasFareKey = !!String(item.fareTypeId ?? "").trim();
        const useByGroup =
            isAccommodation &&
            hasFareKey &&
            shouldCheckAvailabilityByGroup(
                item.apiProviderId ?? 0,
                item.groupFaresForAvailabilityCheck ?? false,
            );
        const list = useByGroup
            ? await toursApi.checkAvailByProductAndRange(
                  String(item.productId),
                  String(item.fareTypeId),
                  startDate,
                  moment(startDate).add(days, "days").toDate(),
              )
            : await toursApi.checkAvailRange(ppdid, timeId, startDate, days);
        const wantedDates = new Set<string>();
        for (let i = 0; i < days; i++) {
            wantedDates.add(moment(startDate).add(i, "days").format("YYYY-MM-DD"));
        }
        type AvailRow = {
            BookingDate?: string;
            NumAvailable?: number | string;
            BookingTimeID?: string | number;
        };
        const rows: AvailRow[] = Array.isArray(list) ? (list as AvailRow[]) : [];
        const matches = rows.filter((a) => {
            const d = a?.BookingDate ? moment(a.BookingDate).format("YYYY-MM-DD") : "";
            return wantedDates.has(d);
        });
        // Record the index of the row matching the item's actual start date
        // so the cart/quote payload can send the real `selectedAvailableIndex`.
        // Sending a hardcoded 0 causes a 400 when the response includes rows
        // for unrelated dates or is sorted differently than expected.
        const wantedStart = moment(startDate).format("YYYY-MM-DD");
        const availIndex = rows.findIndex((a) => {
            const d = a?.BookingDate ? moment(a.BookingDate).format("YYYY-MM-DD") : "";
            return d === wantedStart;
        });
        // Capture the BookingTimeID from the matched row so drag-drop items
        // (which had no time picker) inherit a real slot id. Products with
        // no time component report "0" here, which is the same sentinel we
        // already use for "no time slot".
        const matchedRow = availIndex >= 0 ? rows[availIndex] : undefined;
        const matchedTimeId =
            matchedRow?.BookingTimeID !== undefined && matchedRow?.BookingTimeID !== null
                ? String(matchedRow.BookingTimeID)
                : undefined;
        const haveAllDays = matches.length >= wantedDates.size;
        const allEnough = matches.every((a) => Number(a?.NumAvailable ?? 0) >= item.quantity);
        const ok = matches.length > 0 && haveAllDays && allEnough;
        return {
            itemId,
            version,
            status: ok ? ("available" as const) : ("unavailable" as const),
            message: ok ? undefined : "No availability for the selected date",
            availIndex: availIndex >= 0 ? availIndex : undefined,
            bookingTimeId: matchedTimeId,
        };
    } catch (err) {
        const message = err instanceof Error ? err.message : "Could not verify availability";
        return {
            itemId,
            version,
            status: "unknown" as TripItemAvailability,
            message,
        };
    }
});

export const fetchMyTrips = createAsyncThunk<{
    summaries: ITripSummary[];
    diagnostic: NonNullable<ITripPlannerState["myTripsDiagnostic"]>;
}>(
    "tripPlanner/fetchMyTrips",
    async (_, { rejectWithValue }) => {
        try {
            // Use /quotes (getQuotes), NOT /quotes/all or /quotes/my:
            // - /quotes/all and /quotes/my are gated behind points/commission
            //   agent middleware (a regular agent is blocked outright) and both
            //   filter by redeemer records, so a freshly saved trip (no
            //   redeemers attached yet) never appears.
            // - /quotes returns Quote::where('user_id', userId)->where('is_paid',
            //   false) with no permission gate and no redeemer dependency — the
            //   correct "My Trips" source. `data` is an array, so the [Trip]
            //   title-prefix filter still applies.
            const res = await quoteApi.getQuotes();
            const raw = res.data?.data || [];
            const summaries = quotesToTripSummaries(raw);
            // Diagnostic snapshot — surfaced on the empty-state of My Trips
            // when no rows match. Lets the user see whether /quotes returned 0
            // quotes (auth/account issue), returned quotes whose titles don't
            // look like trips (backend renamed them), or the filter is wrongly
            // excluding them.
            const diagnostic = {
                totalQuotes: raw.length,
                matchedAsTrip: summaries.length,
                sampleTitles: raw
                    .slice(0, 5)
                    .map((q) => (typeof q?.title === "string" ? q.title : "(no title)")),
                fetchedAt: new Date().toISOString(),
            };
            // eslint-disable-next-line no-console
            console.info("[tripPlanner] fetchMyTrips diagnostic", diagnostic);
            return { summaries, diagnostic };
        } catch (e) {
            const message = e instanceof Error ? e.message : "Failed to load trips";
            return rejectWithValue(message);
        }
    },
);

export const loadTripFromQuote = createAsyncThunk<
    ITrip,
    { quoteId: string | number },
    { rejectValue: string }
>("tripPlanner/loadTripFromQuote", async ({ quoteId }, { rejectWithValue }) => {
    try {
        const res = await quoteApi.getQuotePreview(quoteId);
        const body = res.data as { data?: IQuote } | IQuote | undefined;
        const quote: IQuote | undefined =
            body && typeof body === "object" && "data" in body && body.data
                ? body.data
                : (body as IQuote | undefined);
        if (!quote) return rejectWithValue("Quote not found");
        // Best-effort: pull the persisted roster so per-item traveller ticks +
        // optional answers can be re-linked. Legacy quotes / unsupported
        // backends just yield no customers → quoteToTrip falls back gracefully.
        let customers: ICustomerDetail[] | undefined;
        try {
            const custRes = await quoteApi.getCustomers(quoteId);
            customers = custRes.data?.data ?? undefined;
        } catch {
            /* no roster — restore falls back to the derived shape */
        }
        return quoteToTrip(quote, customers);
    } catch (e) {
        return rejectWithValue(extractApiMessage(e, "Failed to load trip"));
    }
});

export const deleteTrip = createAsyncThunk<
    string | number,
    { quoteId: string | number },
    { rejectValue: string }
>("tripPlanner/deleteTrip", async ({ quoteId }, { rejectWithValue }) => {
    try {
        await quoteApi.deleteQuote(quoteId);
        return quoteId;
    } catch (e) {
        return rejectWithValue(extractApiMessage(e, "Failed to delete trip"));
    }
});

/**
 * Reconcile an already-saved quote with the current trip state.
 *
 * Strategy: pair each trip item with an existing quote item that has the
 * same productPricesDetailsId AND start date (first match wins so duplicates
 * disambiguate). Paired items that only differ in quantity get a single
 * batched `updateQuoteItem` call; paired items whose date changed are
 * treated as a remove+add (the update endpoint can't move dates). New trip
 * items are added; existing quote items with no pair are deleted.
 *
 * Deletes run first so removed inventory doesn't double-count against the
 * subsequent add availability checks. Adds run sequentially to keep the
 * saved quote's display order matching the planner's left-to-right order.
 *
 * Exported for testing; not meant to be called outside this slice.
 */
const updateExistingTripQuote = async (
    trip: ITrip,
    quoteId: string | number,
): Promise<void> => {
    const preview = await quoteApi.getQuotePreview(quoteId);
    const previewBody = preview?.data as { data?: IQuote } | IQuote | undefined;
    const existingQuote: IQuote | undefined =
        previewBody && typeof previewBody === "object" && "data" in previewBody && previewBody.data
            ? previewBody.data
            : (previewBody as IQuote | undefined);
    const existingItems = existingQuote?.items ?? [];

    const normalizeDate = (s: string | undefined | null): string => {
        if (!s) return "";
        const m = moment(s, [moment.ISO_8601, "YYYY-MM-DD", "DD-MMM-YYYY"]);
        return m.isValid() ? m.format("YYYY-MM-DD") : String(s);
    };
    const tripDate = (dayIndex: number): string =>
        moment(trip.startDate, "YYYY-MM-DD").add(dayIndex, "days").format("YYYY-MM-DD");

    // Pair trip items to existing items by (ppdid + date). First-match wins
    // so an existing quote with two copies of the same product pairs each
    // copy to a distinct trip slot instead of collapsing them.
    const matchedExistingIds = new Set<number>();
    const toUpdate: IUpdateCartItemData[] = [];
    const toAdd: ITripItem[] = [];

    for (const item of trip.items) {
        const wantedDate = tripDate(item.dayIndex);
        const wantedPpdid = String(item.productPricesDetailsId);
        const match = existingItems.find(
            (ex) =>
                !matchedExistingIds.has(ex.id) &&
                String(ex.product_price_details_id) === wantedPpdid &&
                normalizeDate(ex.start_date) === wantedDate,
        );
        if (match) {
            matchedExistingIds.add(match.id);
            if (match.booking_quantity !== item.quantity) {
                toUpdate.push({
                    cartItemId: match.id,
                    quantity: item.quantity,
                    // updateQuoteItem requires `bookingData`; preserve what's
                    // already on the row so per-traveller info isn't wiped.
                    bookingData: (Array.isArray(match.booking_data)
                        ? match.booking_data
                        : []) as BookingData[],
                });
            }
        } else {
            toAdd.push(item);
        }
    }

    const toDelete = existingItems.filter((ex) => !matchedExistingIds.has(ex.id));

    // Delete first so removed inventory is freed for the subsequent adds.
    for (const ex of toDelete) {
        // eslint-disable-next-line no-await-in-loop
        await quoteApi.deleteQuoteItem({ type: "item", id: ex.id }, quoteId);
    }
    // Sequential to preserve display order (matches the create path).
    for (const item of toAdd) {
        const payload = {
            ...tripItemToAddPayload(item, trip.startDate),
            quoteId: String(quoteId),
        };
        // eslint-disable-next-line no-await-in-loop
        await quoteApi.addQuoteItem(String(quoteId), payload);
    }
    if (toUpdate.length > 0) {
        await quoteApi.updateQuoteItem(toUpdate, quoteId);
    }
};

/**
 * Best-effort persistence of the parts of a trip the item rows can't express:
 * the trip SHAPE (start date + full day count) as the quote's flat trip-shape
 * fields, the traveller ROSTER as quote customers, and each item's per-traveller ticks + optional
 * answers as `booking_data`. Every step is isolated so a failure here can never
 * block the core save — the trip's items are already persisted by the time this
 * runs, and a legacy/unsupported backend simply means the trip reopens with the
 * derived shape instead of the exact one.
 */
const persistTripShapeAndTravellers = async (
    trip: ITrip,
    quoteId: string | number,
): Promise<void> => {
    // 1. Trip shape → flat trip_start_date/trip_duration via the V2 partial
    //    update (isolated; can't break the item save).
    try {
        await quoteApi.updateTripShape(quoteId, buildTripShapeFields(trip));
    } catch (e) {
        extractApiMessage(e, "Failed to persist trip shape");
    }

    // 2. Roster + per-item travellers. Always run (even for an empty roster /
    //    no selections) so that REMOVING travellers or clearing answers on an
    //    existing trip is faithfully persisted — "restore exactly as left" must
    //    reflect removals, not just additions.
    try {
        // Replace the quote's customer list with the FULL roster (not just
        // complete people) so customer_index lines up with roster order and
        // per-item references resolve deterministically on read.
        await quoteApi.setCustomers(quoteId, rosterToCustomerInfos(trip.travellers));

        // Read the customers back to learn each one's stable redeemer id,
        // keyed by the customer_index we just wrote.
        const custRes = await quoteApi.getCustomers(quoteId);
        const customers = custRes.data?.data ?? [];
        const customerByIndex = new Map<number, ICustomerDetail>();
        for (const c of customers) customerByIndex.set(Number(c.customer_index), c);
        const redeemerIdByTravellerId = new Map<string, number>();
        trip.travellers.forEach((t, i) => {
            const c = customerByIndex.get(i);
            const rid = c ? Number(customerStableId(c)) : NaN;
            if (Number.isFinite(rid)) redeemerIdByTravellerId.set(t.id, rid);
        });

        // Pair trip items to their persisted quote items, then write each
        // paired item's traveller booking_data.
        const preview = await quoteApi.getQuotePreview(quoteId);
        const previewBody = preview?.data as { data?: IQuote } | IQuote | undefined;
        const quote: IQuote | undefined =
            previewBody && typeof previewBody === "object" && "data" in previewBody && previewBody.data
                ? previewBody.data
                : (previewBody as IQuote | undefined);
        const quoteItems = quote?.items ?? [];
        const pairs = pairTripItemsToQuoteItems(trip, quoteItems);
        const updates: IUpdateCartItemData[] = [];
        for (const { item, quoteItem } of pairs) {
            // Write every paired item, including ones with zero selected
            // travellers, so a deselection explicitly clears the previously
            // persisted redeemers instead of leaving them to resurrect on load.
            const selected = selectedTravellersForItem(trip, item);
            const bookingData = buildItemBookingData(
                item,
                selected,
                redeemerIdByTravellerId,
                Array.isArray(quoteItem.booking_data) ? quoteItem.booking_data : [],
            );
            updates.push({
                cartItemId: quoteItem.id,
                quantity: quoteItem.booking_quantity ?? item.quantity,
                bookingData,
            });
        }
        if (updates.length > 0) await quoteApi.updateQuoteItem(updates, quoteId);
    } catch (e) {
        extractApiMessage(e, "Failed to persist trip travellers");
    }
};

export const saveTripAsQuote = createAsyncThunk<
    string | number,
    void,
    { state: { tripPlanner: ITripPlannerState }; rejectValue: string }
>("tripPlanner/saveTripAsQuote", async (_, { getState, rejectWithValue }) => {
    const trip = getState().tripPlanner.current;
    if (trip.items.length === 0) return rejectWithValue("Trip has no items to save");
    try {
        // Update-in-place path: the trip was loaded from an existing quote,
        // so saving must reconcile that quote rather than create a duplicate.
        if (trip.id !== undefined && trip.id !== null && String(trip.id) !== "") {
            await updateExistingTripQuote(trip, trip.id);
            await persistTripShapeAndTravellers(trip, trip.id);
            return trip.id;
        }
        const newPayload = tripToNewQuotePayload(trip);
        if (!newPayload) return rejectWithValue("Trip has no items to save");
        // POST /quotes/new/v2 returns the created quote in its response body
        // (the id lives at `data[0].quote_id`). Read it directly — no
        // snapshot/refetch diff needed.
        const created = await quoteApi.newQuote(newPayload);
        const quoteId = extractNewQuoteId(created?.data);
        if (!quoteId) {
            // Diagnostic: surface the actual response shape so we can extend
            // extractNewQuoteId if the backend ever changes it.
            try {
                // eslint-disable-next-line no-console
                console.warn(
                    "[tripPlanner] newQuote returned no id; body =",
                    JSON.stringify(created?.data),
                );
            } catch {
                /* noop */
            }
            return rejectWithValue(
                "Trip saved, but could not read the new id to add the rest of the items. Please reload your trips.",
            );
        }
        const restPayloads = tripToAddItemPayloads(trip, String(quoteId));
        for (const p of restPayloads) {
            // Sequential to preserve display order in the saved quote.
            // eslint-disable-next-line no-await-in-loop
            await quoteApi.addQuoteItem(String(quoteId), p);
        }
        await persistTripShapeAndTravellers(trip, quoteId);
        return quoteId;
    } catch (e) {
        return rejectWithValue(extractApiMessage(e, "Failed to save trip"));
    }
});

export const convertTripToCart = createAsyncThunk<
    void,
    { sessionId?: string } | undefined,
    { state: { tripPlanner: ITripPlannerState }; rejectValue: string }
>("tripPlanner/convertTripToCart", async (arg, { getState, rejectWithValue }) => {
    const trip = getState().tripPlanner.current;
    if (trip.items.length === 0) return rejectWithValue("Trip is empty");
    // Hard requirement: every item must be confirmed available before we
    // touch the live cart. `unavailable` is the obvious blocker, but
    // `unknown`/`checking` items have either failed an availability call or
    // not been validated yet — letting them through would let users check
    // out items the booking system may reject. The UI gates the button on
    // the same condition so this is mostly a defensive backstop.
    const blocker = trip.items.find(
        (i) =>
            i.availabilityStatus === "unavailable" ||
            i.availabilityStatus === "unknown" ||
            i.availabilityStatus === "checking",
    );
    if (blocker) {
        return rejectWithValue(
            blocker.availabilityStatus === "unavailable"
                ? "One or more items are unavailable on the selected dates"
                : "Availability is still being checked. Please wait a moment and try again.",
        );
    }
    // Items without any selected, completed roster travellers are skipped
    // entirely below, so the cart behaves exactly as before for trips that
    // never assigned travellers.
    const itemsWithTravellers = trip.items.filter((i) =>
        selectedTravellersForItem(trip, i).some(isTravellerComplete),
    );

    // Snapshot the cart ids that exist *before* we add this trip's items, so the
    // traveller attachment only ever targets the rows we're about to create.
    // Without this, an older cart row with the same product/price+date could be
    // matched and overwritten. Best-effort: a snapshot failure just falls back
    // to matching against the whole cart (previous behaviour).
    const preExistingCartIds = new Set<number>();
    if (itemsWithTravellers.length > 0) {
        try {
            const pre = await cartApi.getCart(arg?.sessionId);
            for (const c of pre.data?.data ?? []) preExistingCartIds.add(c.id);
        } catch {
            // ignore — fall back to whole-cart matching
        }
    }

    try {
        const payloads = tripToCartPayloads(trip);
        for (const p of payloads) {
            // Sequential so cart-side validation runs per item.
            // eslint-disable-next-line no-await-in-loop
            await cartApi.addCart(p, arg?.sessionId);
        }
    } catch (e) {
        return rejectWithValue(extractApiMessage(e, "Failed to add to cart"));
    }

    // Best-effort: carry per-item traveller details into the freshly-added cart
    // items.
    if (itemsWithTravellers.length > 0) {
        try {
            await attachTravellersToCart(
                trip,
                itemsWithTravellers,
                preExistingCartIds,
                arg?.sessionId,
            );
        } catch (e) {
            // The items are already in the cart — only the traveller pre-fill
            // failed. Log the underlying cause for debugging, but always return
            // the cart-intact message: surfacing the raw API error here would
            // wrongly imply the cart add itself failed.
            extractApiMessage(e, "traveller attach failed");
            return rejectWithValue(
                "Items were added to your cart, but traveller details couldn't be saved automatically. You can enter them at checkout.",
            );
        }
    }
});

/**
 * Attach traveller details captured on trip items to their matching cart items.
 *
 * For each trip item that has at least one completed traveller we:
 *  1. fetch the live cart and find the cart item by product/price + start date
 *     (consuming matches so duplicate items map 1:1),
 *  2. create one redeemer per completed traveller, and
 *  3. write the redeemer ids + the product's optional fields into the cart
 *     item's `bookingData`, preserving any existing per-index data (pickup, etc.).
 *
 * Best-effort by design: callers wrap this so a failure here never undoes the
 * already-successful cart additions.
 */
async function attachTravellersToCart(
    trip: ITrip,
    items: ITripItem[],
    preExistingCartIds: Set<number>,
    sessionId?: string,
): Promise<void> {
    const cartResp = await cartApi.getCart(sessionId);
    const cartItems: ICartItemData[] = cartResp.data?.data ?? [];
    // Track cart item ids we've already mapped so two identical trip items
    // (same product/price on the same day) attach to two distinct cart rows.
    // Seed it with the rows that existed before this conversion so we never
    // overwrite a pre-existing cart row that happens to share product+date.
    const usedCartItemIds = new Set<number>(preExistingCartIds);

    for (const item of items) {
        const itemDate = dateForDayIndex(trip.startDate, item.dayIndex);
        const cartItem = cartItems.find(
            (c) =>
                !usedCartItemIds.has(c.id) &&
                String(c.product_price_details_id) === String(item.productPricesDetailsId) &&
                moment(c.start_date).format("YYYY-MM-DD") === itemDate,
        );
        if (!cartItem) continue;
        usedCartItemIds.add(cartItem.id);

        // The roster people booked onto this product, in selection order. The
        // picker keeps this length == quantity, but defensively pad the slot
        // count so we never under-fill the cart's booking rows.
        const selected = selectedTravellersForItem(trip, item);
        const slotCount = Math.max(item.quantity, selected.length);

        // Create a redeemer per selected, completed traveller (index-aligned).
        // Slots without a completed traveller keep a null so we don't attach a
        // bogus redeemer.
        const redeemerIds: (number | null)[] = [];
        for (let i = 0; i < slotCount; i += 1) {
            const t = selected[i];
            if (!t || !isTravellerComplete(t)) {
                redeemerIds.push(null);
                continue;
            }
            // eslint-disable-next-line no-await-in-loop
            const created = await redeemerApi.AddRedeemer(travellerToCustomerData(t, i), sessionId);
            const redeemer = created.data?.data ?? created.data;
            const redeemerId = Number(redeemer?.id);
            redeemerIds.push(Number.isFinite(redeemerId) ? redeemerId : null);
        }

        const existing: BookingData[] = Array.isArray(cartItem.booking_data)
            ? cartItem.booking_data
            : [];
        const timeId = cartItem.time_id ?? item.bookingTimeId ?? null;
        const fallbackTimeId =
            timeId === null || timeId === undefined ? undefined : String(timeId);
        const bookingData: BookingData[] = Array.from({ length: slotCount }, (_unused, i) => {
            const base = (existing[i] ?? {}) as BookingData;
            const baseRedeemers = Array.isArray(base.redeemers) ? base.redeemers : [];
            const redeemerId = redeemerIds[i];
            const t = selected[i];
            const optionalData = t
                ? cleanOptionalData(item.travellerOptionalData?.[t.id])
                : {};
            return {
                ...base,
                quantityIndex: base.quantityIndex ?? i,
                timeId: base.timeId ?? fallbackTimeId,
                redeemers:
                    redeemerId === null || redeemerId === undefined
                        ? baseRedeemers
                        : [redeemerId],
                optionalData: { ...(base.optionalData ?? {}), ...optionalData },
            };
        });

        // eslint-disable-next-line no-await-in-loop
        await cartApi.updateCartItems(
            [
                {
                    cartItemId: cartItem.id,
                    quantity: cartItem.booking_quantity ?? item.quantity,
                    bookingData,
                },
            ],
            sessionId,
        );
    }
}

// ---- Slice -----------------------------------------------------------------

interface AddItemPayload {
    productId: string;
    tdmsProductId: number;
    name: string;
    productImagePath?: string;
    productClass?: string | null;
    fareTypeId: string | number;
    fareName?: string;
    productPricesDetailsId: string | number;
    /**
     * Live-rate booking keys for externally-sourced accommodation rooms
     * (e.g. Hotelbeds). Carried through — not displayed — so the room can be
     * booked at checkout. Absent for CMS/generic and non-accommodation items.
     */
    defaultRateCode?: string;
    roomCode?: string;
    externalId?: string;
    externalSource?: string;
    /**
     * Availability-endpoint discriminator captured from the product detail so
     * a post-move re-check (`checkTripItemAvailability`) hits the same endpoint
     * the checkout calendar uses. See ITripItem for details.
     */
    apiProviderId?: number;
    groupFaresForAvailabilityCheck?: boolean;
    rrp: number;
    numPax?: number;
    durationDays?: number;
    bookingTimeId?: string | number | null;
    dayIndex: number;
    quantity?: number;
    /**
     * Optional initial availability status. Defaults to "unknown" so the
     * post-drop availability check runs. Pass "available" when the item came
     * from a flow where the user explicitly picked an available date+fare
     * (e.g. the AvailabilityModal "Add to Trip" path), so we don't redo the
     * check the user just performed.
     */
    availabilityStatus?: TripItemAvailability;
    /**
     * Index of the row in the checkAvailRange response that matches the
     * picked date. REQUIRED when `availabilityStatus === "available"` so
     * the recheck can be skipped without losing the index — otherwise
     * `tripItemToAddPayload` falls back to `[0]` and the Save/Add-to-Cart
     * call 400s on dates that aren't first in the response.
     */
    availIndex?: number;
}

/**
 * Drop any per-item traveller references (and their optional data) that no
 * longer point at a roster member. Called whenever the roster shrinks — via
 * the roster editor (`setRosterTravellers`) or the header people count
 * (`setTripMeta`) — so an item can never carry a ghost id.
 */
const pruneItemTravellerRefs = (current: ITrip) => {
    const validIds = new Set(current.travellers.map((t) => t.id));
    current.items.forEach((item) => {
        if (item.travellerIds) {
            item.travellerIds = item.travellerIds.filter((id) => validIds.has(id));
        }
        if (item.travellerOptionalData) {
            for (const id of Object.keys(item.travellerOptionalData)) {
                if (!validIds.has(id)) delete item.travellerOptionalData[id];
            }
        }
    });
};

const tripPlanner = createSlice({
    name: "tripPlanner",
    initialState,
    reducers: {
        setTripMeta: (
            state,
            action: PayloadAction<
                Partial<Pick<ITrip, "name" | "startDate" | "durationDays" | "numTravellers">>
            >,
        ) => {
            const startDateChanged =
                action.payload.startDate !== undefined &&
                action.payload.startDate !== state.current.startDate;
            Object.assign(state.current, action.payload);
            // Keep the roster length in sync with the people count so the
            // header's number field and the roster editor never disagree.
            // Padding/trimming preserves already-entered people (and their ids,
            // so existing per-product selections stay valid).
            if (action.payload.numTravellers !== undefined) {
                state.current.travellers = ensureRosterCount(
                    state.current.travellers,
                    state.current.numTravellers,
                );
                // Trimming the roster can orphan per-item selections, so drop
                // any ids that no longer exist (mirrors setRosterTravellers).
                pruneItemTravellerRefs(state.current);
            }
            // If trip got shorter, push out-of-range items to the last day
            // AND shrink any spans that now extend past the new trip end.
            const max = Math.max(0, state.current.durationDays - 1);
            state.current.items.forEach((it) => {
                let touched = false;
                if (it.dayIndex > max) {
                    it.dayIndex = max;
                    touched = true;
                }
                const maxSpan = Math.max(1, state.current.durationDays - it.dayIndex);
                if (it.durationDays > maxSpan) {
                    it.durationDays = maxSpan;
                    touched = true;
                }
                if (touched || startDateChanged) {
                    it.availabilityStatus = "unknown";
                    it.availabilityVersion += 1;
                }
            });
        },
        addItem: (state, action: PayloadAction<AddItemPayload>) => {
            const p = action.payload;
            const numPax = Math.max(1, p.numPax ?? 1);
            const dayIndex = Math.max(0, Math.min(state.current.durationDays - 1, p.dayIndex));
            // Default quantity to the trip-level travellers count (snapped up
            // to a multiple of numPax — e.g. 3 travellers in a TWIN room
            // (numPax=2) becomes qty=4). Caller can override via payload.
            const travellers = Math.max(1, state.current.numTravellers || numPax);
            const defaultQty = Math.ceil(travellers / numPax) * numPax;
            // Span must fit inside the visible trip — never let a multi-day
            // item bleed past durationDays, otherwise availability checks &
            // cart payloads target dates outside the planned window.
            const maxSpan = Math.max(1, state.current.durationDays - dayIndex);
            const item: ITripItem = {
                id: newTripItemId(),
                productId: p.productId,
                tdmsProductId: p.tdmsProductId,
                name: p.name,
                productImagePath: p.productImagePath,
                productClass: p.productClass ?? null,
                dayIndex,
                durationDays: Math.max(1, Math.min(maxSpan, p.durationDays ?? 1)),
                fareTypeId: p.fareTypeId,
                fareName: p.fareName,
                productPricesDetailsId: p.productPricesDetailsId,
                defaultRateCode: p.defaultRateCode,
                roomCode: p.roomCode,
                externalId: p.externalId,
                externalSource: p.externalSource,
                apiProviderId: p.apiProviderId,
                groupFaresForAvailabilityCheck: p.groupFaresForAvailabilityCheck,
                quantity: Math.max(numPax, p.quantity ?? defaultQty),
                numPax,
                rrp: p.rrp,
                bookingTimeId: p.bookingTimeId ?? null,
                availabilityStatus: p.availabilityStatus ?? "unknown",
                availabilityVersion: 0,
                availIndex: p.availIndex,
            };
            state.current.items.push(item);
        },
        moveItem: (state, action: PayloadAction<{ itemId: string; toDayIndex: number }>) => {
            const item = state.current.items.find((i) => i.id === action.payload.itemId);
            if (!item) return;
            item.dayIndex = Math.max(
                0,
                Math.min(state.current.durationDays - 1, action.payload.toDayIndex),
            );
            // Re-clamp the span at the new position so a multi-day item moved
            // toward the end of the trip can't extend past durationDays.
            const maxSpan = Math.max(1, state.current.durationDays - item.dayIndex);
            item.durationDays = Math.max(1, Math.min(maxSpan, item.durationDays));
            item.availabilityStatus = "unknown";
            item.availabilityVersion += 1;
        },
        removeItem: (state, action: PayloadAction<{ itemId: string }>) => {
            state.current.items = state.current.items.filter((i) => i.id !== action.payload.itemId);
        },
        resizeItem: (
            state,
            action: PayloadAction<{ itemId: string; durationDays: number }>,
        ) => {
            const item = state.current.items.find((i) => i.id === action.payload.itemId);
            if (!item) return;
            const maxSpan = Math.max(1, state.current.durationDays - item.dayIndex);
            item.durationDays = Math.max(1, Math.min(maxSpan, action.payload.durationDays));
            item.availabilityStatus = "unknown";
            item.availabilityVersion += 1;
        },
        /**
         * Grow the trip in either direction so the supplied date falls
         * inside the trip window. Used by the AvailabilityModal "Add to
         * Trip" path so that picking a date outside the planner's current
         * range still lands on a real day instead of silently failing.
         *
         *   - Date earlier than startDate → shift startDate to that date
         *     AND bump every existing item's dayIndex by the shift, so
         *     items keep their original calendar dates. durationDays grows
         *     by the same amount (to preserve the original end date).
         *   - Date past the current end → extend durationDays to cover.
         *   - Both shifts are capped at MAX_TRIP_DAYS (40); if the cap
         *     would be exceeded the action is a no-op (caller should fall
         *     back to clamping or surface an error to the user).
         */
        extendTripToInclude: (state, action: PayloadAction<{ date: string }>) => {
            const target = moment(action.payload.date, ["YYYY-MM-DD", moment.ISO_8601]).startOf("day");
            if (!target.isValid()) return;
            const start = moment(state.current.startDate, "YYYY-MM-DD").startOf("day");
            const dayIndex = target.diff(start, "days");
            const MAX = 40;
            if (dayIndex < 0) {
                const shift = -dayIndex;
                const newDuration = Math.min(MAX, state.current.durationDays + shift);
                // Bail if even the cap can't fit the shift (e.g. user
                // picked a date 50 days before start of an already-long
                // trip). Better to leave state untouched than partially
                // shift items off the visible window.
                if (newDuration < state.current.durationDays + shift) return;
                state.current.startDate = target.format("YYYY-MM-DD");
                state.current.durationDays = newDuration;
                state.current.items.forEach((it) => {
                    it.dayIndex += shift;
                    // Item's calendar date is unchanged so its previously
                    // known availability is still valid — no version bump.
                });
            } else if (dayIndex >= state.current.durationDays) {
                // Symmetric with the early-shift branch: no-op if the
                // required duration would exceed the cap. Don't partially
                // grow the trip and then leave the user with no item added.
                const required = dayIndex + 1;
                if (required > MAX) return;
                state.current.durationDays = required;
            }
        },
        updateItemQuantity: (
            state,
            action: PayloadAction<{ itemId: string; quantity: number }>,
        ) => {
            const item = state.current.items.find((i) => i.id === action.payload.itemId);
            if (!item) return;
            item.quantity = Math.max(item.numPax, action.payload.quantity);
            item.availabilityStatus = "unknown";
            item.availabilityVersion += 1;
            // The per-product picker validates the selection count against the
            // new quantity on open, so we don't force-trim travellerIds here —
            // that would silently discard the user's earlier choices. If the
            // quantity dropped below the selection, the picker flags it.
        },
        /**
         * Replace the trip's traveller roster (the people travelling). Keeps
         * `numTravellers` in lockstep with the roster size so the header count
         * always reflects reality. Per-product selections that reference a
         * removed person are pruned so no item points at a ghost id.
         */
        setRosterTravellers: (
            state,
            action: PayloadAction<{ travellers: ITripTraveller[] }>,
        ) => {
            const roster = action.payload.travellers.map((t) => ({
                ...t,
                id: t.id || newTripItemId(),
            }));
            state.current.travellers = roster;
            state.current.numTravellers = Math.max(1, roster.length);
            pruneItemTravellerRefs(state.current);
        },
        /**
         * Set which roster people are on a product, plus that product's
         * per-person optional/additional field values. The picker commits the
         * whole selection at once (so Cancel discards). Optional data is keyed
         * by traveller id; entries for non-selected people are dropped.
         */
        setItemTravellerSelection: (
            state,
            action: PayloadAction<{
                itemId: string;
                travellerIds: string[];
                optionalData?: Record<string, TripOptionalData>;
            }>,
        ) => {
            const item = state.current.items.find((i) => i.id === action.payload.itemId);
            if (!item) return;
            const rosterIds = new Set(state.current.travellers.map((t) => t.id));
            // Only keep ids that still exist on the roster, de-duped, in order.
            const ids: string[] = [];
            for (const id of action.payload.travellerIds) {
                if (rosterIds.has(id) && !ids.includes(id)) ids.push(id);
            }
            item.travellerIds = ids;
            const selected = new Set(ids);
            const nextOptional: Record<string, TripOptionalData> = {};
            for (const [id, data] of Object.entries(action.payload.optionalData ?? {})) {
                if (!selected.has(id)) continue;
                const cleaned = cleanOptionalData(data);
                if (Object.keys(cleaned).length) nextOptional[id] = cleaned;
            }
            item.travellerOptionalData = nextOptional;
        },
        clearTrip: (state) => {
            state.current = emptyTrip();
            state.saveStage = LoadingStage.NOT_INITIALIZED;
            state.convertStage = LoadingStage.NOT_INITIALIZED;
            state.lastError = undefined;
        },
        loadTrip: (state, action: PayloadAction<ITrip>) => {
            // Normalise onto the roster model and fold any legacy per-item
            // travellers in, so older persisted trips load without breaking.
            state.current = migrateTripTravellers(action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(checkTripItemAvailability.pending, (state, action) => {
                const item = state.current.items.find((i) => i.id === action.meta.arg.itemId);
                if (item && item.availabilityStatus === "unknown") {
                    item.availabilityStatus = "checking";
                }
            })
            .addCase(checkTripItemAvailability.fulfilled, (state, action) => {
                const item = state.current.items.find((i) => i.id === action.payload.itemId);
                // Drop stale results: the item may have been moved/resized/etc.
                // since this request was issued.
                if (!item || item.availabilityVersion !== action.payload.version) return;
                item.availabilityStatus = action.payload.status;
                item.availabilityMessage = action.payload.message;
                item.availIndex = action.payload.availIndex;
                // Only adopt the auto-discovered time when the item doesn't
                // already have one the user picked (via AvailabilityModal or
                // edit modal). Otherwise we'd silently overwrite their choice
                // every time availability is re-validated.
                if (
                    (item.bookingTimeId === null || item.bookingTimeId === undefined) &&
                    action.payload.bookingTimeId !== undefined
                ) {
                    item.bookingTimeId = action.payload.bookingTimeId;
                }
            })
            .addCase(fetchMyTrips.pending, (state) => {
                state.loadStage = LoadingStage.IS_LOADING;
            })
            .addCase(fetchMyTrips.fulfilled, (state, action) => {
                state.savedTrips = action.payload.summaries;
                state.myTripsDiagnostic = action.payload.diagnostic;
                state.loadStage = LoadingStage.LOADING_COMPLETE;
            })
            .addCase(fetchMyTrips.rejected, (state, action) => {
                state.loadStage = LoadingStage.LOADING_FAILURE;
                state.lastError = (action.payload as string) || "Failed to load trips";
                state.myTripsDiagnostic = {
                    totalQuotes: 0,
                    matchedAsTrip: 0,
                    sampleTitles: [],
                    fetchedAt: new Date().toISOString(),
                    error: (action.payload as string) || "Failed to load trips",
                };
            })
            .addCase(saveTripAsQuote.pending, (state) => {
                state.saveStage = LoadingStage.IS_LOADING;
                state.lastError = undefined;
            })
            .addCase(saveTripAsQuote.fulfilled, (state, action) => {
                state.saveStage = LoadingStage.LOADING_COMPLETE;
                state.current.id = action.payload;
            })
            .addCase(saveTripAsQuote.rejected, (state, action) => {
                state.saveStage = LoadingStage.LOADING_FAILURE;
                state.lastError = (action.payload as string) || "Failed to save trip";
            })
            .addCase(convertTripToCart.pending, (state) => {
                state.convertStage = LoadingStage.IS_LOADING;
                state.lastError = undefined;
            })
            .addCase(convertTripToCart.fulfilled, (state) => {
                state.convertStage = LoadingStage.LOADING_COMPLETE;
            })
            .addCase(convertTripToCart.rejected, (state, action) => {
                state.convertStage = LoadingStage.LOADING_FAILURE;
                state.lastError = (action.payload as string) || "Failed to add to cart";
            })
            .addCase(loadTripFromQuote.pending, (state) => {
                state.loadStage = LoadingStage.IS_LOADING;
                state.lastError = undefined;
            })
            .addCase(loadTripFromQuote.fulfilled, (state, action) => {
                state.current = action.payload;
                state.loadStage = LoadingStage.LOADING_COMPLETE;
            })
            .addCase(loadTripFromQuote.rejected, (state, action) => {
                state.loadStage = LoadingStage.LOADING_FAILURE;
                state.lastError = (action.payload as string) || "Failed to load trip";
            })
            .addCase(deleteTrip.fulfilled, (state, action) => {
                const deletedId = String(action.payload);
                state.savedTrips = state.savedTrips.filter(
                    (t) => String(t.id) !== deletedId,
                );
                // Compare via String() — quote ids round-trip through the
                // router as strings even when the API returns numbers.
                if (state.current.id !== undefined && String(state.current.id) === deletedId) {
                    state.current.id = undefined;
                }
            })
            .addCase(deleteTrip.rejected, (state, action) => {
                state.lastError = (action.payload as string) || "Failed to delete trip";
            });
    },
});

export const {
    setTripMeta,
    addItem,
    moveItem,
    removeItem,
    resizeItem,
    updateItemQuantity,
    setRosterTravellers,
    setItemTravellerSelection,
    extendTripToInclude,
    clearTrip,
    loadTrip,
} = tripPlanner.actions;

export const selectTripPlanner = (state: RootState) => state.tripPlanner;
export default tripPlanner.reducer;
